import { ClaimDefinition } from "@credify/api-docs/apiClients/v1";
import type { Claims, Dictionary, Func, OfferCondition } from "../types";

interface IConditionEvaluator {
  Evaluate(claims: Claims): ConditionEvaluationResult;
}

export enum OfferConditionKind {
  UnknownCondition = "UNKNOWN_CONDITION",
  AndCondition = "AND_CONDITION",
  EqualityCondition = "EQUALITY_CONDITION",
  LargerThanCondition = "LARGER_THAN_CONDITION",
  LargerThanOrEqualCondition = "LARGER_THAN_OR_EQUAL_CONDITION",
  LessThanCondition = "LESS_THAN_CONDITION",
  LessThanOrEqualCondition = "LESS_THAN_OR_EQUAL_CONDITION",
  ContainCondition = "CONTAIN_CONDITION",
  InRangeCondition = "IN_RANGE_CONDITION",
}

export class ConditionEvaluationResult {
  public rank: number | undefined;

  public requestedScopes: Array<string> | undefined;

  public usedScopes: Array<string> | undefined;

  public static New(scope?: string): ConditionEvaluationResult {
    const instance = new ConditionEvaluationResult();
    instance.rank = 0;

    if (!scope) {
      instance.requestedScopes = new Array<string>();
      instance.usedScopes = new Array<string>();
    } else {
      instance.requestedScopes = new Array<string>(scope);
      instance.usedScopes = new Array<string>(scope);
    }
    return instance;
  }
}

export class ConditionEvaluator {
  public static New(condition: OfferCondition): IConditionEvaluator {
    switch (condition.kind) {
      case OfferConditionKind.AndCondition:
        return new AndConditionEvaluator(condition.subconditions!);

      case OfferConditionKind.EqualityCondition:
        return new EqualityConditionEvaluator(condition);

      case OfferConditionKind.LargerThanCondition:
        return new LargerThanConditionEvaluator(condition);

      case OfferConditionKind.LargerThanOrEqualCondition:
        return new LargerThanOrEqualConditionEvaluator(condition);

      case OfferConditionKind.LessThanCondition:
        return new LessThanConditionEvaluator(condition);

      case OfferConditionKind.LessThanOrEqualCondition:
        return new LessThanOrEqualConditionEvaluator(condition);

      case OfferConditionKind.ContainCondition:
        return new ContainConditionEvaluator(condition);

      case OfferConditionKind.InRangeCondition:
        return new InRangeConditionEvaluator(condition);

      default:
        return new DefaultConditionEvaluator(condition);
    }
  }

  public static ExtractClaimValue(
    claimDef: ClaimDefinition,
    claims: Claims
  ): Object | null {
    if (!claims.hasOwnProperty(claimDef.scope!.name!)) {
      return null;
    }

    let scopeValue = claims[claimDef.scope!.name!];
    if (scopeValue == null) {
      return null;
    }

    let claimDefinitions = new Array<ClaimDefinition>();
    while (claimDef != null) {
      claimDefinitions.unshift(claimDef);
      claimDef = claimDef.main!;
    }

    let claimValue: Object | null = null;
    let claimValues = scopeValue;
    for (let def of claimDefinitions) {
      if (claimValues == null) {
        return null;
      }

      if (!claimValues.hasOwnProperty(def.name!)) {
        return null;
      }

      claimValue = claimValues[def.name!];
      claimValues = <Dictionary<Object>>claimValue;
    }

    return claimValue;
  }

  public static EvaluateValue<T>(
    claimValue: any,
    evaluator: Func<T[], boolean>,
    conditionValue: any,
    conditionValue2?: any
  ): boolean {
    return evaluator(...[claimValue, conditionValue, conditionValue2]);
  }
}

/* abstract */
class ValueConditionEvaluator {
  public Claim: ClaimDefinition;

  public Value: any;

  public constructor(condition: OfferCondition) {
    this.Claim = condition.claim!;
    this.Value = condition.value;
  }

  protected typesGuard(type: string, itemsNeedToChecks: any[]) {
    for (let item of itemsNeedToChecks) {
      const itemType = typeof item;
      if (itemType !== type) {
        return false;
      }
    }
    return true;
  }

  protected EvaluateValue<T>(
    claims: Claims,
    evaluator: Func<T[], boolean>
  ): ConditionEvaluationResult {
    let result = ConditionEvaluationResult.New(this.Claim.scope!.name!);
    let claimValue = ConditionEvaluator.ExtractClaimValue(this.Claim, claims);
    if (claimValue == null) {
      return result;
    }

    if (ConditionEvaluator.EvaluateValue(claimValue, evaluator, this.Value)) {
      result.rank = 1;
    }

    return result;
  }
}

class EqualityConditionEvaluator
  extends ValueConditionEvaluator
  implements IConditionEvaluator
{
  public constructor(condition: OfferCondition) {
    super(condition);
  }

  public Evaluate(claims: Claims): ConditionEvaluationResult {
    switch (this.Claim.valueType) {
      case "BOOLEAN": {
        return this.EvaluateValue<boolean>(
          claims,
          (...[claimValue, conditionValue]) => {
            if (!this.typesGuard("boolean", [claimValue, conditionValue])) {
              return false;
            }
            return claimValue === conditionValue;
          }
        );
      }
      case "FLOAT":
      case "INTEGER": {
        return this.EvaluateValue<number>(
          claims,
          (...[claimValue, conditionValue]) => {
            if (!this.typesGuard("number", [claimValue, conditionValue])) {
              return false;
            }
            return claimValue === conditionValue;
          }
        );
      }
      case "TEXT": {
        return this.EvaluateValue<string>(
          claims,
          (...[claimValue, conditionValue]) => {
            if (!this.typesGuard("string", [claimValue, conditionValue])) {
              return false;
            }
            return claimValue === conditionValue;
          }
        );
      }

      default:
        return ConditionEvaluationResult.New(this.Claim.scope?.name!);
    }
  }
}

class LessThanConditionEvaluator
  extends ValueConditionEvaluator
  implements IConditionEvaluator
{
  public constructor(condition: OfferCondition) {
    super(condition);
  }

  public Evaluate(claims: Claims): ConditionEvaluationResult {
    switch (this.Claim.valueType) {
      case "FLOAT":
      case "INTEGER": {
        return this.EvaluateValue<number>(
          claims,
          (...[claimValue, conditionValue]) => {
            if (!this.typesGuard("number", [claimValue, conditionValue])) {
              return false;
            }
            return claimValue < conditionValue;
          }
        );
      }

      default:
        return ConditionEvaluationResult.New(this.Claim.scope?.name!);
    }
  }
}

class LessThanOrEqualConditionEvaluator
  extends ValueConditionEvaluator
  implements IConditionEvaluator
{
  public constructor(condition: OfferCondition) {
    super(condition);
  }

  public Evaluate(claims: Claims): ConditionEvaluationResult {
    switch (this.Claim.valueType) {
      case "FLOAT":
      case "INTEGER": {
        return this.EvaluateValue<number>(
          claims,
          (...[claimValue, conditionValue]) => {
            if (!this.typesGuard("number", [claimValue, conditionValue])) {
              return false;
            }
            return claimValue <= conditionValue;
          }
        );
      }

      default:
        return ConditionEvaluationResult.New(this.Claim.scope?.name!);
    }
  }
}

class LargerThanConditionEvaluator
  extends ValueConditionEvaluator
  implements IConditionEvaluator
{
  public constructor(condition: OfferCondition) {
    super(condition);
  }

  public Evaluate(claims: Claims): ConditionEvaluationResult {
    switch (this.Claim.valueType) {
      case "FLOAT":
      case "INTEGER": {
        return this.EvaluateValue<number>(
          claims,
          (...[claimValue, conditionValue]) => {
            if (!this.typesGuard("number", [claimValue, conditionValue])) {
              return false;
            }
            return claimValue > conditionValue;
          }
        );
      }

      default:
        return ConditionEvaluationResult.New(this.Claim.scope?.name!);
    }
  }
}

class LargerThanOrEqualConditionEvaluator
  extends ValueConditionEvaluator
  implements IConditionEvaluator
{
  public constructor(condition: OfferCondition) {
    super(condition);
  }

  public Evaluate(claims: Claims): ConditionEvaluationResult {
    switch (this.Claim.valueType) {
      case "FLOAT":
      case "INTEGER": {
        return this.EvaluateValue<number>(
          claims,
          (...[claimValue, conditionValue]) => {
            if (!this.typesGuard("number", [claimValue, conditionValue])) {
              return false;
            }
            return claimValue >= conditionValue;
          }
        );
      }

      default:
        return ConditionEvaluationResult.New(this.Claim.scope?.name!);
    }
  }
}

class InRangeConditionEvaluator
  extends ValueConditionEvaluator
  implements IConditionEvaluator
{
  public UpperValue: any;

  public constructor(condition: OfferCondition) {
    super(condition);

    this.UpperValue = condition.upper;
  }

  public Evaluate(claims: Claims): ConditionEvaluationResult {
    switch (this.Claim.valueType) {
      case "FLOAT":
      case "INTEGER": {
        return this.EvaluateValue<number>(
          claims,
          (...[claimValue, conditionValue, upperValue]) => {
            if (!this.typesGuard("number", [claimValue, conditionValue])) {
              return false;
            }

            return conditionValue <= claimValue && claimValue <= upperValue;
          }
        );
      }

      default:
        return ConditionEvaluationResult.New(this.Claim.scope?.name!);
    }
  }

  protected EvaluateValue<T>(
    claims: Claims,
    evaluator: Func<T[], boolean>
  ): ConditionEvaluationResult {
    let result = ConditionEvaluationResult.New(this.Claim.scope?.name!);
    let claimValue = ConditionEvaluator.ExtractClaimValue(this.Claim, claims);
    if (claimValue == null) {
      return result;
    }

    if (
      ConditionEvaluator.EvaluateValue(
        claimValue,
        evaluator,
        this.Value,
        this.UpperValue
      )
    ) {
      result.rank = 1;
    }

    return result;
  }
}

class ContainConditionEvaluator
  extends ValueConditionEvaluator
  implements IConditionEvaluator
{
  public constructor(condition: OfferCondition) {
    super(condition);
  }

  public Evaluate(claims: Claims): ConditionEvaluationResult {
    return this.EvaluateValue<string>(
      claims,
      (...[claimValue, conditionValue]) => {
        if (!this.typesGuard("string", [claimValue, conditionValue])) {
          return false;
        }
        return claimValue.includes(conditionValue);
      }
    );
  }
}

class DefaultConditionEvaluator
  extends ValueConditionEvaluator
  implements IConditionEvaluator
{
  public constructor(condition: OfferCondition) {
    super(condition);
  }

  public Evaluate(claims: Claims): ConditionEvaluationResult {
    return ConditionEvaluationResult.New(this.Claim.scope?.name!);
  }
}

class LogicalConditionEvaluator {
  public Children: Array<IConditionEvaluator>;

  public constructor(subconditions: Array<OfferCondition>) {
    this.Children =
      subconditions != null
        ? subconditions.map((x) => ConditionEvaluator.New(x))
        : [];
  }
}

class AndConditionEvaluator
  extends LogicalConditionEvaluator
  implements IConditionEvaluator
{
  public constructor(subconditions: Array<OfferCondition>) {
    super(subconditions);
  }

  public Evaluate(claims: Claims): ConditionEvaluationResult {
    let result = ConditionEvaluationResult.New();
    if (this.Children.length == 0) {
      return result;
    }

    result.rank = 1;
    for (let child of this.Children) {
      let childResult = child.Evaluate(claims);
      result.requestedScopes = [
        ...result.requestedScopes!,
        ...childResult.requestedScopes!,
      ];
      //  main condition is failed already, stop processing further
      if (result.rank == 0) {
        continue;
      }

      result.usedScopes = [...result.usedScopes!, ...childResult.usedScopes!];

      if (childResult.rank! <= 0) {
        //  now the child condition failed, main condition is set as failed as well
        result.rank = 0;
      }
    }

    return result;
  }
}
